/**
 * ListingCard Component
 *
 * Card displaying listing preview with image carousel, title, location, and price.
 * Supports swiping/clicking through photos without entering the listing.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface Listing {
  id: string;
  title: string;
  photos: Array<{ url: string; thumbnail?: string; caption?: string }>;
  propertyType: string;
  location: {
    city: string;
    area?: string;
    state?: string;
    country: string;
  };
  pricing: {
    nightlyRate: number;
    currency: string;
  };
  rating?: number;
  reviewCount?: number;
  likeCount?: number;
  bedrooms: number;
  beds: number;
  maxGuests: number;
  isFeatured?: boolean;
  hostId?: string;
  host?: {
    displayName: string;
    photoURL?: string;
  };
}

interface ListingCardProps {
  listing: Listing;
  onSave?: (listingId: string) => void;
  isSaved?: boolean;
  variant?: 'default' | 'compact';
}

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=600&q=80';

export const ListingCard: React.FC<ListingCardProps> = ({
  listing,
  onSave,
  isSaved = false,
  variant = 'default',
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const photos = listing.photos?.length > 0
    ? listing.photos
    : [{ url: FALLBACK_IMAGE }];

  const hasMultiplePhotos = photos.length > 1;

  const handleSaveClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onSave?.(listing.id);
  };

  const handlePrevImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
  };

  const handleDotClick = (e: React.MouseEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex(index);
  };

  // Build location text, avoiding repetition like "Lebanon, Lebanon"
  const city = listing.location?.city?.trim() || '';
  const area = listing.location?.area?.trim() || listing.location?.state?.trim() || '';

  const getLocationText = () => {
    const isGenericCity = !city || city.toLowerCase() === 'lebanon';
    const isGenericArea = !area || area.toLowerCase() === 'lebanon';

    if (isGenericCity && isGenericArea) {
      return 'Lebanon';
    }
    if (isGenericCity) {
      return `${area}, Lebanon`;
    }
    if (isGenericArea || area.toLowerCase() === city.toLowerCase()) {
      return `${city}, Lebanon`;
    }
    return `${city}, ${area}`;
  };

  const locationText = getLocationText();

  return (
    <Link
      to={`/listing/${listing.id}`}
      className="group block"
    >
      {/* Image Container with Carousel */}
      <div className="relative aspect-[4/3] rounded-xl overflow-hidden mb-3">
        {/* Current Image - Use thumbnail for faster loading */}
        <img
          src={photos[currentImageIndex]?.thumbnail || photos[currentImageIndex]?.url || FALLBACK_IMAGE}
          alt={listing.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />

        {/* Navigation Arrows - Only show if multiple photos */}
        {hasMultiplePhotos && (
          <>
            {/* Left Arrow */}
            <button
              onClick={handlePrevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white hover:scale-110"
              aria-label="Previous image"
            >
              <ChevronLeftIcon className="w-4 h-4 text-gray-800" />
            </button>

            {/* Right Arrow */}
            <button
              onClick={handleNextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white hover:scale-110"
              aria-label="Next image"
            >
              <ChevronRightIcon className="w-4 h-4 text-gray-800" />
            </button>
          </>
        )}

        {/* Property Type Badge */}
        <span className="absolute top-3 left-3 px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-gray-700 capitalize">
          {listing.propertyType.replace('_', ' ')}
        </span>

        {/* Featured Badge */}
        {listing.isFeatured && (
          <span className="absolute top-3 left-24 px-3 py-1 bg-primary-500 text-white rounded-full text-xs font-medium">
            Featured
          </span>
        )}

        {/* Save Button + like count */}
        <button
          onClick={handleSaveClick}
          className="absolute top-3 right-3 flex items-center gap-1 px-2 py-2 rounded-full bg-white/80 backdrop-blur-sm hover:bg-white transition-colors z-10"
          aria-label={isSaved ? 'Remove from saved' : 'Save listing'}
        >
          <HeartIcon
            className={`w-5 h-5 ${
              isSaved
                ? 'fill-red-500 text-red-500'
                : 'text-gray-600 hover:text-gray-900'
            }`}
            filled={isSaved}
          />
          {(listing.likeCount ?? 0) > 0 && (
            <span className="text-xs font-semibold text-gray-700 pr-0.5">
              {listing.likeCount}
            </span>
          )}
        </button>

        {/* Image Dots - Show actual photo count */}
        {photos.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {photos.slice(0, 5).map((_, index) => (
              <button
                key={index}
                onClick={(e) => handleDotClick(e, index)}
                className={`w-1.5 h-1.5 rounded-full transition-all ${
                  index === currentImageIndex
                    ? 'bg-white w-2'
                    : 'bg-white/60 hover:bg-white/80'
                }`}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
            {photos.length > 5 && (
              <span className="w-1.5 h-1.5 rounded-full bg-white/40" />
            )}
          </div>
        )}

        {/* Host Avatar */}
        {listing.host && (
          <div className="absolute bottom-3 right-3 w-8 h-8 rounded-full border-2 border-white shadow-md overflow-hidden bg-primary-500">
            {listing.host.photoURL ? (
              <img
                src={listing.host.photoURL}
                alt={listing.host.displayName}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                }}
              />
            ) : null}
            <span className={`w-full h-full flex items-center justify-center text-white font-bold text-sm ${listing.host.photoURL ? 'hidden' : ''}`}>
              {listing.host.displayName?.[0]?.toUpperCase() || 'H'}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="space-y-1">
        {/* Title & Rating */}
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-gray-900 truncate flex-1">
            {listing.title}
          </h3>
          {listing.rating !== undefined && listing.rating > 0 && (
            <div className="flex items-center gap-1 shrink-0">
              <StarIcon className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              <span className="text-sm font-medium">
                {listing.rating.toFixed(1)}
              </span>
              {listing.reviewCount !== undefined && listing.reviewCount > 0 && (
                <span className="text-sm text-gray-500">
                  ({listing.reviewCount} {listing.reviewCount === 1 ? 'review' : 'reviews'})
                </span>
              )}
            </div>
          )}
        </div>

        {/* Location */}
        <p className="text-sm text-gray-500">{locationText}</p>

        {/* Property Details */}
        {variant === 'default' && (
          <p className="text-sm text-gray-500">
            {listing.bedrooms} bedroom{listing.bedrooms !== 1 ? 's' : ''} · {listing.beds} bed{listing.beds !== 1 ? 's' : ''} · Up to {listing.maxGuests} guests
          </p>
        )}

        {/* Price */}
        <p className="text-sm pt-1">
          <span className="font-semibold text-gray-900">
            ${listing.pricing.nightlyRate}
          </span>
          <span className="text-gray-500"> / night</span>
        </p>
      </div>
    </Link>
  );
};

// Icons
const HeartIcon: React.FC<{ className?: string; filled?: boolean }> = ({
  className,
  filled,
}) => (
  <svg
    className={className}
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={filled ? 0 : 2}
      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
    />
  </svg>
);

const StarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
);

const ChevronLeftIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRightIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

export default ListingCard;
